"use client";

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import {
  ArrowLeft,
  Search,
  Upload,
  Loader2,
  Plus,
  X,
  Play,
  ChevronUp,
  ChevronDown,
  MessageSquare,
  Save,
} from "lucide-react";
import { ReviewComment } from "../shared/ReviewComment";
import { format } from "date-fns";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { AGEING_ANALYSIS_PROCEDURE_PREFIX } from "@/constants/procedure-modules";
import { DocumentManager } from "../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";

const INFERENCE_QUESTION =
  "Document the inference and conclusion from the above procedure performed.";

interface AgeingAnalysisModuleProps {
  onBack?: () => void;
  area?: string;
  section?: string;
  workspaceName?: string;
  workspacePrefix?: string;
  /** Full stored procedure name, e.g. "Ageing Analysis - Vendor ledger" */
  procedureName?: string;
}

interface ExcelColumn {
  name: string;
  value: string;
}

// Format numeric cell for display: Indian numbering, no decimals (rounded)
function formatTableCellNumber(value: string): string {
  if (value == null || value === "") return value;
  const cleaned = String(value).replace(/,/g, "").trim();
  const num = parseFloat(cleaned);
  if (!Number.isFinite(num)) return value;
  return Math.round(num).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function sanitizeAgeingProcedureFilePart(name: string): string {
  const s = name
    .trim()
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  return s || "Untitled";
}

/** Parse numeric cell from ageing CSV (commas stripped; values may use . as decimal from export) */
function parseAgeingNumericCell(value: string | undefined): number {
  if (value == null || value === "") return 0;
  const cleaned = String(value).replace(/,/g, "").trim();
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : NaN;
}

function columnIsNumericForAgeing(dataRows: string[][], colIndex: number): boolean {
  if (dataRows.length === 0) return false;
  let sawValue = false;
  for (const row of dataRows) {
    const cell = row[colIndex];
    if (cell == null || cell === "") continue;
    sawValue = true;
    if (!Number.isFinite(parseAgeingNumericCell(cell))) return false;
  }
  return sawValue;
}

interface AgeingBracketSummary {
  labels: string[];
  totals: number[];
  percentages: number[];
}

/**
 * From pivot CSV: label columns (non-numeric) then bucket columns + Grand Total.
 * Totals = sum per column; % = share of summed Grand Total column if present, else sum of all numeric totals.
 */
function computeAgeingBracketSummary(
  headers: string[],
  dataRows: string[][]
): AgeingBracketSummary | null {
  if (!headers.length || !dataRows.length) return null;

  const numericIndices: number[] = [];
  for (let j = 0; j < headers.length; j++) {
    if (columnIsNumericForAgeing(dataRows, j)) numericIndices.push(j);
  }
  if (numericIndices.length === 0) return null;

  const labels = numericIndices.map((j) => (headers[j] ?? "").trim() || `Column ${j}`);
  const totals = numericIndices.map((j) =>
    dataRows.reduce((sum, row) => sum + parseAgeingNumericCell(row[j]), 0)
  );

  const grandIdxInNumeric = numericIndices.findIndex(
    (j) => headers[j]?.trim().toLowerCase() === "grand total"
  );
  const denominator =
    grandIdxInNumeric >= 0 ? totals[grandIdxInNumeric] : totals.reduce((a, b) => a + b, 0);

  const percentages = totals.map((t) => (denominator > 0 ? (t / denominator) * 100 : 0));

  return { labels, totals, percentages };
}

export default function AgeingAnalysisModule({
  onBack,
  area = "Execution",
  section = "",
  workspaceName = "",
  workspacePrefix = "",
  procedureName = "",
}: AgeingAnalysisModuleProps) {
  const { isArchivalMode } = useArchivalMode();

  // File search states (mirroring IPE Testing section 1)
  const [excelColumns, setExcelColumns] = useState<ExcelColumn[]>([]);
  const [isLoadingExcelColumns, setIsLoadingExcelColumns] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [referenceValue, setReferenceValue] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");

  // Ageing Analysis specific states
  const [dateColumnName, setDateColumnName] = useState<string>("");
  const [cutoffDate, setCutoffDate] = useState<Date>();
  const [userBuckets, setUserBuckets] = useState<number[]>([]);
  const [newBucketValue, setNewBucketValue] = useState<string>("");
  const [amountColumn, setAmountColumn] = useState<string>("");
  const [displayRows, setDisplayRows] = useState<string[]>([]);
  
  // Processing states
  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  // Table states
  const [tableData, setTableData] = useState<string[][]>([]);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [tableHeaders, setTableHeaders] = useState<string[]>([]);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [tableError, setTableError] = useState<string | null>(null);
  const [modifiedTableData, setModifiedTableData] = useState<string[][]>([]);
  const [modifiedTableHeaders, setModifiedTableHeaders] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);

  const displayProcedureTitle =
    procedureName ||
    (section || workspaceName ? `Ageing Analysis - ${section || workspaceName}` : "Ageing Analysis");

  const ageingProcedureSuffix = useMemo(() => {
    if (!procedureName) return "";
    if (procedureName.startsWith(AGEING_ANALYSIS_PROCEDURE_PREFIX)) {
      return procedureName.slice(AGEING_ANALYSIS_PROCEDURE_PREFIX.length).trim();
    }
    if (procedureName === "Ageing Analysis") return "";
    return "";
  }, [procedureName]);

  const executionCsvFileName = useMemo(() => {
    if (!workspacePrefix || !section) return "";
    const prefixUpper = workspacePrefix.toUpperCase().replace(/-/g, "_");
    const filePart = ageingProcedureSuffix
      ? sanitizeAgeingProcedureFilePart(ageingProcedureSuffix)
      : "Ageing";
    return `Execution_${prefixUpper}_${filePart}.csv`;
  }, [workspacePrefix, section, ageingProcedureSuffix]);

  const outputFileBase = useMemo(
    () => executionCsvFileName.replace(/\.csv$/i, ""),
    [executionCsvFileName]
  );

  // Generate question ID for a section (unique per procedure instance)
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    const ws = workspaceName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    const proc = (procedureName || "")
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `${ws}_AGEING_${proc}_${normalizedSection}`;
  };

  const ageingBracketSummary = useMemo(
    () => computeAgeingBracketSummary(modifiedTableHeaders, modifiedTableData),
    [modifiedTableHeaders, modifiedTableData]
  );

  const ageingChartData = useMemo(() => {
    if (!ageingBracketSummary) return [];
    return ageingBracketSummary.labels.map((label, i) => ({
      bracket: label.length > 20 ? `${label.slice(0, 18)}…` : label,
      bracketFull: label,
      total: ageingBracketSummary.totals[i],
      percentage: Math.round(ageingBracketSummary.percentages[i] * 100) / 100,
    }));
  }, [ageingBracketSummary]);

  const tableTotals = useMemo(() => {
    if (!modifiedTableHeaders.length || !modifiedTableData.length) {
      return { columnTotals: [] as number[] };
    }

    const columnTotals = modifiedTableHeaders.map((_, colIdx) =>
      modifiedTableData.reduce((sum, row) => {
        const n = parseAgeingNumericCell(row[colIdx]);
        return Number.isFinite(n) ? sum + n : sum;
      }, 0)
    );

    return { columnTotals };
  }, [modifiedTableHeaders, modifiedTableData]);

  const [setupWorkflowOpen, setSetupWorkflowOpen] = useState(false);

  const phaseFormatted = useMemo(() => {
    if (area === "ConcludingProcedures") return "ConcludingProcedures";
    return area.charAt(0).toUpperCase() + area.slice(1).toLowerCase();
  }, [area]);

  const inferenceItemId = useMemo(() => {
    const wp = (workspacePrefix || "WS").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    const proc = (procedureName || section || "AGEING")
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `${wp}_${proc}_AGEING_INFERENCE`;
  }, [workspacePrefix, procedureName, section]);

  const responsesFileName = useMemo(
    () => (workspacePrefix ? `${phaseFormatted}_${workspacePrefix}_Responses.json` : ""),
    [phaseFormatted, workspacePrefix]
  );

  const documentSectionKey = useMemo(() => {
    const p =
      area === "ConcludingProcedures"
        ? "concludingprocedures"
        : area === "Planning"
          ? "planning"
          : "execution";
    const ws = workspaceName || section;
    return ws ? `${p}-substantive:${ws}` : "";
  }, [area, workspaceName, section]);

  const [companyName, setCompanyName] = useState("");
  const [inferenceQuestion, setInferenceQuestion] = useState<{
    textResponse: { html: string; plainText: string } | null;
    documents: Array<{
      id: string;
      type: "textbox" | "linked" | "uploaded";
      name: string;
      content?: string;
      url?: string;
    }>;
  }>({ textResponse: null, documents: [] });
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch {
        setCompanyName("ABC Private Limited");
      }
    };
    void loadCompanyName();
  }, []);

  useEffect(() => {
    if (!responsesFileName) return;
    const loadInference = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(responsesFileName);
        if (result?.success && result?.data?.substantiveProcedures) {
          const saved = result.data.substantiveProcedures[inferenceItemId];
          if (saved) {
            localStorage.removeItem(`textbox_${inferenceItemId}`);
            localStorage.removeItem(`documents_${inferenceItemId}`);
            let textResponse: { html: string; plainText: string } | null = null;
            if (saved.textResponse) {
              textResponse = {
                html: saved.textResponse.html || saved.textResponse.plainText || "",
                plainText:
                  saved.textResponse.plainText ||
                  (saved.textResponse.html
                    ? String(saved.textResponse.html).replace(/<[^>]*>/g, "")
                    : ""),
              };
              if (textResponse.html) {
                localStorage.setItem(`textbox_${inferenceItemId}`, textResponse.html);
              }
            }
            const documents = (saved.documents || []).map((docName: string) => ({
              id: `doc_${docName}`,
              type: "linked" as const,
              name: docName,
            }));
            localStorage.setItem(`documents_${inferenceItemId}`, JSON.stringify(documents));
            setInferenceQuestion({ textResponse, documents });
          } else {
            localStorage.removeItem(`textbox_${inferenceItemId}`);
            localStorage.removeItem(`documents_${inferenceItemId}`);
            setInferenceQuestion({ textResponse: null, documents: [] });
          }
        }
      } catch (e) {
        console.warn("Failed to load ageing inference:", e);
      }
    };
    void loadInference();
  }, [responsesFileName, inferenceItemId]);

  const saveInferenceQuestion = useCallback(async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus("error");
      return;
    }
    if (!workspacePrefix || !responsesFileName) {
      setInferenceSaveError("Workspace prefix is missing");
      setInferenceSaveStatus("error");
      return;
    }

    setInferenceSaveStatus("saving");
    setInferenceSaveError(null);

    try {
      const documentManagers = document.querySelectorAll("[data-document-manager]");
      for (const manager of Array.from(documentManagers)) {
        const id = manager.getAttribute("data-item-id");
        if (id) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${id}`];
          if (typeof autoSaveMethod === "function") autoSaveMethod();
        }
      }

      await new Promise((r) => setTimeout(r, 1500));

      const sectionToFind = `${phaseFormatted}_${workspacePrefix}_${inferenceItemId}`;
      let documentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            documentNames = dbResult.data
              .map((file: any) => file.name || "")
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (e) {
        console.warn(`Failed to query db.json for ${sectionToFind}:`, e);
      }

      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(responsesFileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (e) {
        console.warn(`Failed to download ${responsesFileName}`, e);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }
      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      const textboxFromLs = localStorage.getItem(`textbox_${inferenceItemId}`);
      const hasTb = textboxFromLs && textboxFromLs.trim().length > 0;
      let textResponseData: { html: string; plainText: string } | null = null;
      if (hasTb) {
        textResponseData = {
          html: textboxFromLs!,
          plainText: textboxFromLs!.replace(/<[^>]*>/g, ""),
        };
      } else if (inferenceQuestion.textResponse) {
        textResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceItemId] = {
        selectedOption: "",
        textResponse: textResponseData,
        documents: documentNames,
        table: [],
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: responsesFileName,
        referenceValue: `${phaseFormatted} ${workspacePrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear,
        sectionList: [`${phaseFormatted}_${workspacePrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference");
      }

      setInferenceSaveStatus("saved");
      setTimeout(() => setInferenceSaveStatus("idle"), 3000);
    } catch (error) {
      console.error("Failed to save ageing inference:", error);
      setInferenceSaveStatus("error");
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
    }
  }, [
    companyName,
    workspacePrefix,
    responsesFileName,
    inferenceItemId,
    phaseFormatted,
    inferenceQuestion.textResponse,
  ]);

  // Auto-trigger search on mount
  useEffect(() => {
    console.log(" AgeingAnalysisModule mounted/updated:", { area, section, workspaceName, workspacePrefix });
    searchForFiles();
  }, [section]);

  // Log when area or section changes
  useEffect(() => {
    console.log(" AgeingAnalysisModule props:", { area, section, workspaceName, workspacePrefix });
  }, [area, section, workspaceName, workspacePrefix]);

  const searchForFiles = async () => {
    if (!section) return;
    
    setFileSearchStatus("searching");
    console.log(" Starting file search...");
    
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        // Use workspace prefix to determine the file search prefix
        // For Trade Payables it's "Execution_TP", so we use area + workspacePrefix
        // If workspacePrefix is not available, fall back to constructing from section
        let searchPrefix = "";
        if (workspacePrefix) {
          const prefixUpper = workspacePrefix.toUpperCase().replace("-", "_");
          searchPrefix = `${area}_${prefixUpper}`;
        } else if (section) {
          const sectionPrefix = section.replace(/\s+/g, "_").toUpperCase();
          searchPrefix = `${area}_${sectionPrefix}`;
        } else {
          console.warn("No workspace prefix or section provided for file search");
          setFileSearchStatus("not_found");
          return;
        }
        
        const result = await window.sharePointAPI.loadComcFiles(searchPrefix);
        console.log(" SharePoint API result:", result);
        
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          
          if (files.length > 0) {
            console.log(" Files found:", files);
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            console.log(" No files found");
            setFileSearchStatus("not_found");
          }
        } else {
          console.log(" API call failed:", result.error);
          setFileSearchStatus("not_found");
        }
      } else {
        console.error("SharePoint API not available");
        setFileSearchStatus("not_found");
      }
    } catch (e) {
      console.error("Error searching for files:", e);
      setFileSearchStatus("not_found");
    }
  };

  const uploadFile = async () => {
    setUploadStatus("uploading");
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(',')[1];
                resolve(base64);
              };
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(file);
            });

            const fyYear = getFY_YEAR();
            if (!fyYear) {
              throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
            }

            if (window.sharePointAPI?.uploadFile) {
              // Determine section list based on section name
              const sectionList = section ? [section] : [];
              
              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: referenceValue,
                folderName: "client",
                fyYear: fyYear,
                sectionList: sectionList
              });

              if (result.success) {
                setSelectedFile(file.name);
                setFoundFiles([file.name]);
                setFileSearchStatus("found");
                setUploadStatus("completed");
              } else {
                setUploadStatus("error");
              }
            } else {
              setUploadStatus("error");
            }
          } catch (error) {
            setUploadStatus("error");
          }
        }
      };
      input.click();
    } catch (e) {
      setUploadStatus("error");
    }
  };

  const loadExcelColumns = async () => {
    if (!selectedFile) return;
    setIsLoadingExcelColumns(true);
    console.log(" Loading columns for file:", selectedFile);
    
    try {
      if (window.payroll?.loadExcelColumns) {
        console.log("Using direct payroll.loadExcelColumns method...");
        const result = await window.payroll.loadExcelColumns(selectedFile);
        
        console.log(" Column loading result:", result);
        
        if (result.ok && result.columns) {
          const columns = result.columns.map((col: string) => ({ name: col, value: col }));
          setExcelColumns(columns);
          console.log(" Columns loaded:", columns);
        } else {
          console.error("Excel columns loading failed:", result.error);
        }
      } else {
        console.error("Payroll API not available");
      }
    } catch (error) {
      console.error("Error loading Excel columns:", error);
    } finally {
      setIsLoadingExcelColumns(false);
    }
  };

  const addBucket = () => {
    const value = parseFloat(newBucketValue);
    if (!isNaN(value) && value > 0 && !userBuckets.includes(value)) {
      setUserBuckets(prev => [...prev, value].sort((a, b) => a - b));
      setNewBucketValue("");
    }
  };

  const removeBucket = (index: number) => {
    setUserBuckets(prev => prev.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      addBucket();
    }
  };

  const canRunAgeingAnalysis = () => {
    return (
      !!executionCsvFileName &&
      !!selectedFile &&
      !!dateColumnName &&
      !!cutoffDate &&
      userBuckets.length > 0 &&
      !!amountColumn &&
      displayRows.length > 0 &&
      outputFileBase.trim().length > 0
    );
  };

  const handleDisplayRowToggle = (columnName: string) => {
    setDisplayRows(prev => {
      if (prev.includes(columnName)) {
        // Remove from list
        return prev.filter(col => col !== columnName);
      } else {
        // Add to end of list (preserving order)
        return [...prev, columnName];
      }
    });
  };

  const moveDisplayRow = (index: number, direction: "up" | "down") => {
    setDisplayRows(prev => {
      const newRows = [...prev];
      if (direction === "up" && index > 0) {
        [newRows[index - 1], newRows[index]] = [newRows[index], newRows[index - 1]];
      } else if (direction === "down" && index < newRows.length - 1) {
        [newRows[index], newRows[index + 1]] = [newRows[index + 1], newRows[index]];
      }
      return newRows;
    });
  };

  const runAgeingAnalysis = async () => {
    if (!canRunAgeingAnalysis()) return;
    setProcessingStatus("running");

    try {
      if (window.payroll?.run) {
        // Automatically append .csv if not present
        let finalFileName = outputFileBase.trim();
        if (finalFileName && !finalFileName.toLowerCase().endsWith('.csv')) {
          finalFileName = finalFileName + '.csv';
        }

        // First, check if the file already exists (using SharePoint API like document upload)
        console.log(" Checking if file exists:", finalFileName);
        let fileExists = false;
        
        try {
          // Use loadDocumentListBySection to check for files in juggernaut folder
          // We'll search by section key and then check if our file name exists
          if (window.sharePointAPI?.loadDocumentListBySection) {
            // Get section code to construct section key
            let sectionCode = "";
            if (window.sharePointAPI?.downloadSectionsJson) {
              const sectionsResult = await window.sharePointAPI.downloadSectionsJson();
              if (sectionsResult?.success && sectionsResult.data && sectionsResult.data[area]) {
                sectionCode = sectionsResult.data[area][section] || "";
              }
            }
            
            // Fallback: construct section code from section name if not found
            if (!sectionCode) {
              sectionCode = section.replace(/\s+/g, "_").toUpperCase();
            }
            
            const sectionKey = `${area}_${sectionCode}_Ageing`;
            console.log(" Checking files for section key:", sectionKey);
            
            const result = await window.sharePointAPI.loadDocumentListBySection(sectionKey);
            
            if (result?.success && result.data && Array.isArray(result.data)) {
              // Check if our file name exists in the list
              fileExists = result.data.some((item: any) => {
                const fileName = item.name || item;
                return fileName === finalFileName;
              });
              
              console.log(" File existence check result:", fileExists, "for file:", finalFileName);
            }
          }
        } catch (checkError) {
          console.warn(" Failed to check file existence, proceeding anyway:", checkError);
          // If check fails, proceed anyway (don't block user)
        }
        
        // CRITICAL: If file exists, show error and STOP - do not proceed
        if (fileExists === true) {
          console.error(" File already exists! Stopping execution immediately.");
          setProcessingStatus("error");
          // Show error message
          alert("This file name cannot be used. A file with this name already exists.");
          return; // CRITICAL: Return early to prevent ageing analysis from running
        }
        
        console.log(" File check passed, proceeding with ageing analysis");

        // Proceed with ageing analysis (only reaches here if file doesn't exist or check failed)
        const result = await window.payroll.run("execute_ageing_analysis_module", {
          inputFiles: [],
          options: {
            area: area,
            section: section,
            input_excel_file_name: selectedFile,
            date_col_name: dateColumnName,
            cutoff_date: cutoffDate ? format(cutoffDate, "MM/dd/yyyy") : "",
            buckets: userBuckets,
            rows: displayRows,
            value_col: amountColumn,
            output_csv_name: finalFileName,
          },
        });

        if (result.ok) {
          setProcessingStatus("completed");
        } else {
          setProcessingStatus("error");
        }
      } else {
        setProcessingStatus("error");
      }
    } catch (e) {
      console.error("Error running ageing analysis:", e);
      setProcessingStatus("error");
    }
  };

  const parseCSV = (csvText: string): { headers: string[]; rows: string[][] } => {
    const lines = csvText.split('\n').filter(line => line.trim().length > 0);
    if (lines.length === 0) {
      return { headers: [], rows: [] };
    }
    
    // Parse headers
    const headers = lines[0].split(',').map(h => h.trim());
    
    // Parse rows
    const rows: string[][] = [];
    for (let i = 1; i < lines.length; i++) {
      const row = lines[i].split(',').map(cell => cell.trim());
      rows.push(row);
    }
    
    return { headers, rows };
  };

  const deleteColumn = (columnIndex: number) => {
    // Remove column from headers
    const newHeaders = modifiedTableHeaders.filter((_, idx) => idx !== columnIndex);
    setModifiedTableHeaders(newHeaders);
    
    // Remove column from all rows
    const newData = modifiedTableData.map(row => row.filter((_, idx) => idx !== columnIndex));
    setModifiedTableData(newData);
  };

  const convertToCSV = (headers: string[], data: string[][]): string => {
    // Replace commas with periods in data values to prevent CSV format issues
    // Also handle quotes and newlines by escaping them
    const processCSVValue = (value: string): string => {
      // Replace commas with periods
      let processed = String(value).replace(/,/g, '.');
      
      // Still escape quotes and newlines if present
      if (processed.includes('"') || processed.includes('\n')) {
        processed = `"${processed.replace(/"/g, '""')}"`;
      }
      
      return processed;
    };
    
    // Create CSV rows
    const csvRows: string[] = [];
    
    // Add headers (also replace commas in headers)
    csvRows.push(headers.map(processCSVValue).join(','));
    
    // Add data rows (replace commas with periods in all data)
    data.forEach(row => {
      csvRows.push(row.map(processCSVValue).join(','));
    });
    
    return csvRows.join('\n');
  };

  const handleUpload = async () => {
    if (modifiedTableHeaders.length === 0 || modifiedTableData.length === 0) {
      setUploadMessage("No data to upload");
      setTimeout(() => setUploadMessage(null), 3000);
      return;
    }

    setIsUploading(true);
    setUploadMessage(null);

    try {
      if (!window.payroll?.saveTempFile || !window.payroll?.run) {
        throw new Error("Payroll API not available");
      }

      // Convert table data to CSV
      const csvContent = convertToCSV(modifiedTableHeaders, modifiedTableData);
      
      // Convert CSV to base64
      const base64Content = btoa(unescape(encodeURIComponent(csvContent)));
      
      // Save CSV to temp file
      const fileName = `Ageing.csv`;
      const saveResult = await window.payroll.saveTempFile(fileName, base64Content);
      
      if (!saveResult.ok || !saveResult.filePath) {
        throw new Error(saveResult.error || 'Failed to save temp file');
      }

      // Get FY_YEAR
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const targetFileName = executionCsvFileName;
      if (!targetFileName) {
        throw new Error("Execution CSV file name is not configured");
      }
      console.log(" Uploading to file:", targetFileName);

      // Call Python script to upload
      const result = await window.payroll.run("execute_ageing_analysis_upload_csv", {
        inputFiles: [],
        options: {
          area: area,
          section: section,
          reference_value: "",
          folder_name: "juggernaut",
          fy_year: fyYear,
          section_list: section ? [section] : [],
          file_name: saveResult.filePath,
          target_file_name: targetFileName // Pass the target file name to overwrite existing file
        },
      });

      if (result.ok && result.runId) {
        // Listen for progress updates
        const unsubscribe = window.payroll.onProgress((payload: any) => {
          if (payload.runId === result.runId) {
            if (payload.status === "success") {
              setUploadMessage(" CSV uploaded successfully!");
              setIsUploading(false);
              unsubscribe();
              setTimeout(() => setUploadMessage(null), 5000);
            } else if (payload.status === "error") {
              setUploadMessage(` Upload failed: ${payload.error || "Unknown error"}`);
              setIsUploading(false);
              unsubscribe();
              setTimeout(() => setUploadMessage(null), 5000);
            }
          }
        });

        // Timeout fallback
        setTimeout(() => {
          if (isUploading) {
            setUploadMessage(" Upload is taking longer than expected...");
          }
        }, 10000);
      } else {
        throw new Error(result.error || "Failed to start upload");
      }
    } catch (error) {
      console.error("Error uploading CSV:", error);
      setUploadMessage(` Upload failed: ${error instanceof Error ? error.message : "Unknown error"}`);
      setIsUploading(false);
      setTimeout(() => setUploadMessage(null), 5000);
    }
  };

  const loadTable = useCallback(async (fileName?: string) => {
    setIsLoadingTable(true);
    setTableError(null);
    try {
      if (!window.payroll?.run) {
        setTableError("Payroll API not available");
        setTableData([]);
        setTableHeaders([]);
        setModifiedTableData([]);
        setModifiedTableHeaders([]);
        setIsLoadingTable(false);
        return;
      }

      if (!window.cloud?.readTempFile) {
        setTableError("File reading API not available");
        setTableData([]);
        setTableHeaders([]);
        setModifiedTableData([]);
        setModifiedTableHeaders([]);
        setIsLoadingTable(false);
        return;
      }

      // Call Python script to download CSV
      console.log(" loadTable called with fileName:", fileName);
      const result = await window.payroll.run("execute_ageing_analysis_download_csv", {
        inputFiles: [],
        options: {
          area: area,
          section: section,
          local_dir: "", // Empty string will use temp directory in Python script
          file_name: fileName || "", // Pass the specific file name if provided
        },
      });
      console.log(" Download script started, runId:", result.runId);

      if (!result.ok || !result.runId) {
        setTableError(result.error || "Failed to start download");
        setTableData([]);
        setTableHeaders([]);
        setModifiedTableData([]);
        setModifiedTableHeaders([]);
        setIsLoadingTable(false);
        return;
      }

      // Wait for script to complete, then read file directly from temp directory
      await new Promise(resolve => setTimeout(resolve, 8000));

      try {
        // Get temp directory and read file directly
        if (!window.payroll?.getTempDir) {
          throw new Error("getTempDir API not available");
        }

        const tempDirResult = await window.payroll.getTempDir();
        if (!tempDirResult.ok || !tempDirResult.tempDir) {
          throw new Error(tempDirResult.error || "Failed to get temp directory");
        }

        const tempDir = tempDirResult.tempDir;
        // Use the fileName parameter that was passed to loadTable (the clicked file)
        // This is the exact file name from the list
        const expectedFileName =
          fileName ||
          executionCsvFileName ||
          (() => {
            const sectionCode = section.replace(/\s+/g, "_").toUpperCase();
            return `${area}_${sectionCode}_Ageing.csv`;
          })();
        console.log(" Reading file from temp directory:", expectedFileName);
        const fullPath = `${tempDir}\\${expectedFileName}`;
        console.log(" Full path:", fullPath);
        
        // Read the CSV file
        const readResult = await window.cloud.readTempFile(fullPath);
        
        if (!readResult.success || !readResult.content) {
          setTableError(readResult.error || "Failed to read CSV file");
          setTableData([]);
          setTableHeaders([]);
          setModifiedTableData([]);
          setModifiedTableHeaders([]);
          setIsLoadingTable(false);
          return;
        }

        // Parse and display CSV
        const { headers, rows } = parseCSV(readResult.content);
        
        setTableHeaders(headers);
        setTableData(rows);
        // Initialize modified data with original data
        setModifiedTableHeaders(headers);
        setModifiedTableData(rows);
      } catch (error) {
        setTableError(error instanceof Error ? error.message : "Unknown error");
        setTableData([]);
        setTableHeaders([]);
        setModifiedTableData([]);
        setModifiedTableHeaders([]);
      } finally {
        setIsLoadingTable(false);
      }
    } catch (error) {
      setTableError(error instanceof Error ? error.message : "Unknown error");
      setTableData([]);
      setTableHeaders([]);
      setModifiedTableData([]);
      setModifiedTableHeaders([]);
      setIsLoadingTable(false);
    }
  }, [area, section, executionCsvFileName]);

  useEffect(() => {
    if (!executionCsvFileName || !section) return;
    void loadTable(executionCsvFileName);
  }, [executionCsvFileName, section, loadTable]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">{displayProcedureTitle}</h2>
          <p className="text-gray-400">
            Configure ageing analysis parameters and run analysis
            {executionCsvFileName ? (
              <span className="block mt-1 text-white/50 text-sm">
                Table file: {executionCsvFileName}
              </span>
            ) : null}
          </p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/40 overflow-hidden">
        <CardHeader className="p-0 border-b border-white/10">
          <button
            type="button"
            onClick={() => setSetupWorkflowOpen((o) => !o)}
            aria-expanded={setupWorkflowOpen}
            className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left hover:bg-white/[0.04] transition-colors"
          >
            <div className="min-w-0 pr-2">
              <CardTitle className="text-white text-base sm:text-lg leading-snug">
                Configure & run ageing analysis
              </CardTitle>
              <p className="text-sm text-white/50 mt-1">
                Steps 1–7: search files, map columns, set buckets, run analysis
              </p>
            </div>
            <ChevronDown
              className={`h-5 w-5 text-white/55 shrink-0 transition-transform duration-200 ${setupWorkflowOpen ? "rotate-180" : ""}`}
              aria-hidden
            />
          </button>
        </CardHeader>
        {setupWorkflowOpen && (
          <CardContent className="space-y-4 pt-4 pb-6 px-6">
      {/* Section 1: File Search (Exact copy from IPE Testing) */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Search className="h-5 w-5 text-blue-500" />
              Step 1: Search for Existing Files
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Existing Files"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {fileSearchStatus === "found" && (
              <span className="text-green-400 font-bold"> FOUND!</span>
            )}
            {fileSearchStatus === "not_found" && (
              <span className="text-yellow-400">No files found</span>
            )}
          </div>

          {fileSearchStatus === "found" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Select File</Label>
                <Select value={selectedFile} onValueChange={setSelectedFile}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select file" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {foundFiles.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedFile && (
                <div className="flex items-center gap-3">
                  <Button onClick={loadExcelColumns} disabled={!selectedFile || isLoadingExcelColumns} className="bg-blue-600 hover:bg-blue-700 text-white">
                    {isLoadingExcelColumns ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading Columns...</>
                    ) : (
                      "Load Columns"
                    )}
                  </Button>
                  {excelColumns.length > 0 && (
                    <span className="text-green-400 text-sm">
                       {excelColumns.length} columns loaded
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {fileSearchStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">File not found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Reference Value (optional)</Label>
                  <Input
                    value={referenceValue}
                    onChange={(e) => setReferenceValue(e.target.value)}
                    placeholder="Enter reference value"
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
                <Button onClick={uploadFile} disabled={uploadStatus === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">
                  {uploadStatus === "uploading" ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Upload File</>
                  )}
                </Button>
                {uploadStatus === "completed" && (
                  <span className="text-green-400">File uploaded successfully!</span>
                )}
                {uploadStatus === "error" && (
                  <span className="text-red-400">Upload failed</span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Date Column Selection */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Step 2: Select Date Column</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 2: Select Date Column"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-white">Date Column Name</Label>
            <Select value={dateColumnName} onValueChange={setDateColumnName} disabled={excelColumns.length === 0}>
              <SelectTrigger>
                <SelectValue placeholder={excelColumns.length === 0 ? "Load columns first" : "Select date column"} />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-black/90 text-white">
                {excelColumns.map((col) => (
                  <SelectItem key={col.value} value={col.value}>{col.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Cutoff Date */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Step 3: Set Cutoff Date</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 3: Set Cutoff Date"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-white">Cutoff Date</Label>
            <Input
              type="date"
              value={cutoffDate ? format(cutoffDate, "yyyy-MM-dd") : ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value) {
                  setCutoffDate(new Date(value));
                } else {
                  setCutoffDate(undefined);
                }
              }}
              className="w-full bg-black/40 border-white/10 text-white"
            />
          </div>
        </CardContent>
      </Card>

      {/* User Buckets */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Step 4: Configure Ageing Buckets</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 4: Configure Ageing Buckets"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-white">User Buckets (Days)</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                value={newBucketValue}
                onChange={(e) => setNewBucketValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter number of days"
                className="border-white/10 bg-black/40 text-white"
                min="1"
              />
              <Button onClick={addBucket} disabled={!newBucketValue || isNaN(parseFloat(newBucketValue))} className="bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {userBuckets.length > 0 && (
            <div className="space-y-2">
              <Label className="text-white">Current Buckets:</Label>
              <div className="flex flex-wrap gap-2">
                {userBuckets.map((bucket, index) => (
                  <div key={index} className="flex items-center gap-2 px-3 py-1 bg-blue-600/20 border border-blue-600/30 rounded-md">
                    <span className="text-blue-300">{bucket} days</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBucket(index)}
                      className="h-4 w-4 p-0 hover:bg-red-600/20 text-red-400"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-400">
                Buckets: [{userBuckets.join(", ")}]
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Amount Column */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Step 5: Select Amount Column</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 5: Select Amount Column"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-white">Amount Column</Label>
            <Select value={amountColumn} onValueChange={setAmountColumn} disabled={excelColumns.length === 0}>
              <SelectTrigger>
                <SelectValue placeholder={excelColumns.length === 0 ? "Load columns first" : "Select amount column"} />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-black/90 text-white">
                {excelColumns.map((col) => (
                  <SelectItem key={col.value} value={col.value}>{col.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Display Rows */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Step 6: Select Display Rows</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 6: Select Display Rows"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-white">Display Rows (Order matters - select in desired order)</Label>
            <div className="space-y-2 max-h-60 overflow-y-auto border border-white/10 rounded-md p-3 bg-black/20">
              {excelColumns.length === 0 ? (
                <p className="text-gray-400 text-sm">Load columns first</p>
              ) : (
                excelColumns.map((col) => (
                  <div key={col.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`row-${col.value}`}
                      checked={displayRows.includes(col.value)}
                      onCheckedChange={() => handleDisplayRowToggle(col.value)}
                      className="border-white/20"
                    />
                    <label
                      htmlFor={`row-${col.value}`}
                      className="text-sm text-white cursor-pointer flex-1"
                    >
                      {col.name}
                    </label>
                  </div>
                ))
              )}
            </div>
          </div>
          
          {displayRows.length > 0 && (
            <div className="space-y-2">
              <Label className="text-white">Selected Rows (in order):</Label>
              <div className="space-y-2">
                {displayRows.map((colName, index) => {
                  const col = excelColumns.find(c => c.value === colName);
                  return (
                    <div key={colName} className="flex items-center gap-2 px-3 py-2 bg-blue-600/20 border border-blue-600/30 rounded-md">
                      <span className="text-blue-300 font-medium w-6 text-center">{index + 1}</span>
                      <span className="text-blue-300 flex-1">{col?.name || colName}</span>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveDisplayRow(index, "up")}
                          disabled={index === 0}
                          className="h-6 w-6 p-0 hover:bg-blue-600/30 text-blue-300"
                        >
                          <ChevronUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveDisplayRow(index, "down")}
                          disabled={index === displayRows.length - 1}
                          className="h-6 w-6 p-0 hover:bg-blue-600/30 text-blue-300"
                        >
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDisplayRowToggle(colName)}
                          className="h-6 w-6 p-0 hover:bg-red-600/20 text-red-400"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Run Ageing Analysis */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Play className="h-5 w-5 text-green-500" />
              Run Ageing Analysis
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Run Ageing Analysis"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Button 
              onClick={runAgeingAnalysis} 
              disabled={!canRunAgeingAnalysis() || processingStatus === "running"}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {processingStatus === "running" ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running Analysis...</>
              ) : (
                <><Play className="h-4 w-4 mr-2" /> Run Ageing Analysis</>
              )}
            </Button>
            
            {processingStatus === "completed" && (
              <span className="text-green-400 font-medium"> Analysis completed successfully!</span>
            )}
            {processingStatus === "error" && (
              <span className="text-red-400 font-medium"> Analysis failed</span>
            )}
          </div>
          
          {processingStatus === "completed" && (
            <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
              <p className="text-green-400 text-sm">
                Ageing analysis completed!
              </p>
            </div>
          )}
          
          {processingStatus === "error" && (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm">
                An error occurred while running the ageing analysis. Please try again or contact support.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
          </CardContent>
        )}
      </Card>

      {/* Bracket analysis — separate section from detail table */}
      {modifiedTableData.length > 0 &&
        modifiedTableHeaders.length > 0 &&
        ageingBracketSummary &&
        ageingChartData.length > 0 && (
          <Card className="border-white/10 bg-black/40">
            <CardHeader>
              <CardTitle className="text-white">Bracket analysis (from loaded CSV)</CardTitle>
              <p className="text-sm text-white/50 mt-1">
                Totals and % of grand total by ageing bucket (from the grid below)
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col lg:flex-row gap-6 lg:items-stretch">
                <div className="w-full lg:w-1/2 min-w-0 lg:border-r border-white/10 lg:pr-6 pb-6 lg:pb-0 border-b lg:border-b-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10 bg-white/5 hover:bg-white/5">
                          <TableHead className="text-white/80 font-medium w-[140px] min-w-[120px]">
                            &nbsp;
                          </TableHead>
                          {ageingBracketSummary.labels.map((label, idx) => (
                            <TableHead
                              key={idx}
                              className="text-white/80 font-medium text-right whitespace-nowrap min-w-[88px]"
                            >
                              {label}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow className="border-white/10 hover:bg-white/5">
                          <TableCell className="text-white/90 font-medium">Total amount</TableCell>
                          {ageingBracketSummary.totals.map((t, idx) => (
                            <TableCell key={idx} className="text-right text-white/90 tabular-nums">
                              {Math.round(t).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow className="border-white/10 hover:bg-white/5">
                          <TableCell className="text-white/90 font-medium">Percentage</TableCell>
                          {ageingBracketSummary.percentages.map((p, idx) => (
                            <TableCell key={idx} className="text-right text-amber-300/90 tabular-nums">
                              {p.toFixed(2)}%
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                  <p className="text-xs text-white/45 mt-2">
                    Percentages use the summed <span className="text-white/60">Grand Total</span> column as
                    100% when present; otherwise shares are vs. the sum of all numeric columns.
                  </p>
                </div>
                <div className="w-full lg:w-1/2 min-w-0 flex flex-col lg:pl-2">
                  <div className="h-[min(22rem,50vh)] w-full min-h-[240px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={ageingChartData}
                        margin={{ top: 8, right: 12, left: 4, bottom: 48 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                        <XAxis
                          dataKey="bracket"
                          tick={{ fill: "rgba(255,255,255,0.65)", fontSize: 11 }}
                          tickLine={false}
                          axisLine={{ stroke: "rgba(255,255,255,0.2)" }}
                          interval={0}
                          angle={-32}
                          textAnchor="end"
                          height={56}
                        />
                        <YAxis
                          yAxisId="amt"
                          tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 11 }}
                          tickLine={false}
                          axisLine={{ stroke: "rgba(255,255,255,0.2)" }}
                          tickFormatter={(v) =>
                            typeof v === "number" && Math.abs(v) >= 1e7
                              ? `${(v / 1e7).toFixed(1)}Cr`
                              : typeof v === "number"
                                ? v.toLocaleString("en-IN", { notation: "compact", maximumFractionDigits: 1 })
                                : String(v)
                          }
                          width={56}
                        />
                        <YAxis
                          yAxisId="pct"
                          orientation="right"
                          tick={{ fill: "rgba(251,191,36,0.85)", fontSize: 11 }}
                          tickLine={false}
                          axisLine={{ stroke: "rgba(255,255,255,0.2)" }}
                          tickFormatter={(v) => `${v}%`}
                          domain={[0, "auto"]}
                          width={44}
                        />
                        <Tooltip
                          content={({ active, payload }) =>
                            active && payload?.length ? (
                              <div className="rounded-lg border border-white/20 bg-black/95 px-3 py-2 text-white shadow-xl text-sm">
                                <p className="font-medium text-white mb-1">
                                  {(payload[0]?.payload as { bracketFull?: string })?.bracketFull}
                                </p>
                                <p className="text-blue-300">
                                  Total:{" "}
                                  {Math.round(
                                    (payload[0]?.payload as { total?: number })?.total ?? 0
                                  ).toLocaleString("en-IN")}
                                </p>
                                <p className="text-amber-300">
                                  % of grand total:{" "}
                                  {(
                                    (payload[0]?.payload as { percentage?: number })?.percentage ?? 0
                                  ).toFixed(2)}
                                  %
                                </p>
                              </div>
                            ) : null
                          }
                        />
                        <Legend
                          wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                          formatter={(value) => <span className="text-white/80">{value}</span>}
                        />
                        <Bar
                          yAxisId="amt"
                          dataKey="total"
                          name="Total amount"
                          fill="#3b82f6"
                          radius={[4, 4, 0, 0]}
                          maxBarSize={48}
                        />
                        <Line
                          yAxisId="pct"
                          type="monotone"
                          dataKey="percentage"
                          name="% of grand total"
                          stroke="#fbbf24"
                          strokeWidth={2}
                          dot={{ r: 3, fill: "#fbbf24", strokeWidth: 0 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

      {documentSectionKey && responsesFileName ? (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <CardTitle className="text-white text-base sm:text-lg leading-snug">
                {INFERENCE_QUESTION}
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                {inferenceSaveStatus === "saved" && (
                  <span className="text-sm text-green-400">Saved!</span>
                )}
                {inferenceSaveStatus === "error" && inferenceSaveError && (
                  <span className="text-sm text-red-400 max-w-[200px] truncate" title={inferenceSaveError}>
                    Error
                  </span>
                )}
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void saveInferenceQuestion()}
                  disabled={
                    inferenceSaveStatus === "saving" || !companyName || isArchivalMode
                  }
                  className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                >
                  {inferenceSaveStatus === "saving" ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {inferenceSaveStatus === "error" && inferenceSaveError && (
              <p className="text-sm text-red-400 mb-3">{inferenceSaveError}</p>
            )}
            <DocumentManager
              sectionKey={documentSectionKey}
              groupKey="ageing-analysis-module"
              itemId={inferenceItemId}
              itemQuestion={INFERENCE_QUESTION}
              itemTextResponse={inferenceQuestion.textResponse}
              itemDocuments={inferenceQuestion.documents || []}
              onTextResponseChange={(textResponse) =>
                setInferenceQuestion((prev) => ({ ...prev, textResponse }))
              }
              onDocumentsChange={(documents) =>
                setInferenceQuestion((prev) => ({ ...prev, documents }))
              }
              disabled={isArchivalMode}
            />
          </CardContent>
        </Card>
      ) : null}

      {/* Ageing table (loaded automatically from Execution_<prefix>_<procedure>.csv) */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <CardTitle className="text-white">Ageing Analysis Table</CardTitle>
              <p className="text-sm text-white/50 mt-1">
                Loaded CSV grid — edit columns and update to SharePoint
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Ageing Analysis Table"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!executionCsvFileName && (
            <p className="text-amber-400 text-sm">
              Workspace prefix is missing; cannot resolve the CSV file name. Check Sections.json for this
              workspace.
            </p>
          )}
          {isLoadingTable && (
            <div className="flex items-center gap-2 text-white/70">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading table from SharePoint…
            </div>
          )}
          {tableError && !isLoadingTable && (
            <div className="text-amber-400/90 text-sm">
              {tableError}
              <span className="block mt-1 text-white/50">
                If you have not run ageing analysis yet, this is expected. Run the analysis above to create{" "}
                {executionCsvFileName || "the CSV"}.
              </span>
            </div>
          )}
          {!tableError && !isLoadingTable && tableData.length > 0 && (
            <div className="text-green-400 text-sm">Table loaded ({tableData.length} rows)</div>
          )}

          {modifiedTableData.length > 0 && modifiedTableHeaders.length > 0 && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button
                  onClick={handleUpload}
                  disabled={isUploading || modifiedTableHeaders.length === 0 || !executionCsvFileName}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {isUploading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Update Table</>
                  )}
                </Button>
              </div>

              {uploadMessage && (
                <div
                  className={`p-3 rounded-md border ${
                    uploadMessage.includes("failed") || uploadMessage.includes("Failed")
                      ? "border-red-500/20 bg-red-500/10 text-red-300"
                      : "border-green-500/20 bg-green-500/10 text-green-300"
                  }`}
                >
                  <span className="text-sm">{uploadMessage}</span>
                </div>
              )}

              <div className="border border-white/10 rounded-lg overflow-auto max-h-[600px]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 bg-white/5">
                      {modifiedTableHeaders.map((header, idx) => (
                        <TableHead key={idx} className="text-white/80 font-medium sticky top-0 bg-black/80 z-10">
                          <div className="flex items-center justify-between gap-2">
                            <span>{header}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteColumn(idx)}
                              className="h-6 w-6 p-0 hover:bg-red-600/20 text-red-400"
                              title="Delete column"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {modifiedTableData.map((row, rowIdx) => (
                      <TableRow key={rowIdx} className="border-white/10 hover:bg-white/5">
                        {row.map((cell, cellIdx) => (
                          <TableCell key={cellIdx} className="text-white/90">
                            {formatTableCellNumber(cell) || cell || ""}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    <TableRow className="border-white/10 bg-black sticky bottom-0 z-20">
                      {tableTotals.columnTotals.map((total, idx) => (
                        <TableCell key={`total-${idx}`} className="text-amber-300 font-semibold bg-black">
                          {Math.round(total).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Step 1: Search for Existing Files")]: "Step 1: Search for Existing Files",
              [getSectionQuestionId("Step 2: Select Date Column")]: "Step 2: Select Date Column",
              [getSectionQuestionId("Step 3: Set Cutoff Date")]: "Step 3: Set Cutoff Date",
              [getSectionQuestionId("Step 4: Configure Ageing Buckets")]: "Step 4: Configure Ageing Buckets",
              [getSectionQuestionId("Step 5: Select Amount Column")]: "Step 5: Select Amount Column",
              [getSectionQuestionId("Step 6: Select Display Rows")]: "Step 6: Select Display Rows",
              [getSectionQuestionId("Run Ageing Analysis")]: "Run Ageing Analysis",
              [getSectionQuestionId("Ageing Analysis Table")]: "Ageing Analysis Table",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}

